import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../core/services/api.service';
import { ToastService } from '../core/services/toast.service';
import { FilesRequest, PersonQuery } from '../models/FilesRequest';
import { DocumentationPack } from '../models/OpenData.DocumentationPack';
import { CookieUser } from '../models/Security.CookieUser';
import { LabeledValue } from 'src/app/models/LabeledValue';

@Injectable({
    providedIn: 'root'
})
export class UtilsService {

    private cookieUser: CookieUser | undefined;
    private version: string | undefined;

    constructor(
        private readonly translateService: TranslateService,
        private readonly apiService: ApiService,
        private readonly http: HttpClient,
        private readonly toastService: ToastService
    ) { }

    async getVersion(): Promise<string> {
        if (!this.version) {
            this.version = await this.apiService.get<string>('version');
        }
        return this.version;
    }

    range(size: number, startAt = 0, step = 1): number[] {
        return [...Array(size).keys()].map(i => startAt + i * step);
    }

    translateBoolean(value: boolean): string {
        return value ? this.translateService.instant('BOOLEAN.TRUE') : this.translateService.instant('BOOLEAN.FALSE');
    }

    convertConsentUrlToConsentUrlBoxed(consentUrl: string): string {
        return consentUrl.replace('/widget/', '/widget-boxed/');
    }

    async getCurrentUserHomePath(): Promise<string> {
        const roles = (await this.getCookieUser()).roles;
        let homePath = '/login';

        if (!roles || !roles.includes('WEB_OPENDATA') || roles.length < 1) return homePath;

        /*if (roles.includes('WEB_OPENDATA_AUTONOMOS') || roles.includes('WEB_OPENDATA_AUTONOMOS_GESTOR')) {
            homePath = '/freelancers';
        }*/
        if (roles.includes('WEB_OPENDATA_TARJETAS') || roles.includes('WEB_OPENDATA_TARJETAS_GESTOR')) {
            homePath = '/cards';
        } else if (roles.includes('WEB_OPENDATA_ESTANDAR') || roles.includes('WEB_OPENDATA_ESTANDAR_GESTOR')) {
            homePath = '/standard';
        } else if (roles.includes('WEB_OPENDATA_INNOVACION') || roles.includes('WEB_OPENDATA_INNOVACION_GESTOR') ||
                    roles.includes('WEB_OPENDATA_AUTONOMOS') || roles.includes('WEB_OPENDATA_AUTONOMOS_GESTOR')) {
            homePath = '/innovation';
        } else if (roles.includes('WEB_OPENDATA_EXPERIAN_ADMIN')) {
            homePath = '/innovation';
        }

        return homePath;
    }

    async getCookieUser(): Promise<CookieUser> {
        if (!this.cookieUser) {
            this.cookieUser = await this.apiService.get<CookieUser>('users/current-user');
        }
        return this.cookieUser;
    }

    cleanCookieUser(): void {
        this.cookieUser = undefined;
    }

    formatLocalYYYYMMDD(date: Date): string {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    }


    formatLocalYYYYMMDDhhmmss(date: Date) {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        return `${year}-${month}-${day}-${hours}-${minutes}-${seconds}`;
    }

    isStringNullOrEmpty(str: string): boolean {
        return !str || str.trim().length === 0;
    }

    async downloadZIP(req: FilesRequest): Promise<void> {
        try {
            const zipName = `${req.detail.personQuery.queryId}_${this.formatLocalYYYYMMDDhhmmss(new Date())}`;
            const blob = await firstValueFrom(this.http.post('api/files', req, { responseType: 'blob' }));
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = zipName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch(e) {
            if (e.status === 404){
                this.toastService.showError('TOAST.ERROR', 'REQUEST_DETAIL.DOCUMENTS.NOT_FOUND', true);
            } else {
                this.toastService.showError(`Error ${e.status} (${e.statusText})`, e.status, false);
            }
        }
    }

    /**
     * Get the home id based on user roles
     * @returns 1 for Freelancers, 2 for cards, 3 for standard and 0 if no home id is found
     */
    async getHomeId(): Promise<number> {
        const roles = (await this.getCookieUser()).roles;
        //if (roles.includes('WEB_OPENDATA_AUTONOMOS') || roles.includes('WEB_OPENDATA_AUTONOMOS_GESTOR')) return 1;
        if (roles.includes('WEB_OPENDATA_TARJETAS') || roles.includes('WEB_OPENDATA_TARJETAS_GESTOR')) return 2;
        if (roles.includes('WEB_OPENDATA_ESTANDAR') || roles.includes('WEB_OPENDATA_ESTANDAR_GESTOR')) return 3;
        return 0;
    }

    async getUserDocumentationPacks(): Promise<DocumentationPack[]> {
        const user = await this.getCookieUser();
        return user.documentationPacks || [];
    }

    async getHelpTexts(): Promise<Record<string, Record<string, string>>> {
        const user = await this.getCookieUser();
        return user.helpTexts || {};
    }

    async getAllowedWidgetLanguages(): Promise<LabeledValue[]> {
        const user = await this.getCookieUser();
        return user.allowWidgetLanguages || [];
    }

    async isWidgetLanguageSelectorVisible(): Promise<boolean> {
        const user = await this.getCookieUser();
        return user.showWidgetLanguageSelector || false;
    }

    async areStandardDatesRequired(): Promise<boolean> {
        const user = await this.getCookieUser();
        return user.standardDatesRequired ?? true;
    }

    async getSubscriberName(): Promise<string> {
        const user = await this.getCookieUser();
        return user.subscriberData.name;
    }

    arrayBufferToBase64(buffer: ArrayBuffer): string {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }

    showQRButton(pq: PersonQuery): boolean {
        if (pq.belenderCircuit === 6) return false;
	    return pq.status === 'Created' || pq.status === 'Processing';
    }

    getFullName(pq: PersonQuery): string {
        const parts = [pq.firstName, pq.firstSurname, pq.secondSurname].filter(p => !!p && String(p).trim().length > 0).map(p => String(p).trim());
        return parts.join(' ');
    }

    async isExperianAdmin(): Promise<boolean> {
        const roles = (await this.getCookieUser()).roles;
        return roles.includes('WEB_OPENDATA_EXPERIAN_ADMIN');
    }
}
